import React, { useEffect, useMemo, useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  phoneErrorMessage,
  toE164,
  validatePhone,
} from "../../utils/validatePhone.js";

export default function PhoneField({
  id,
  name,
  value = "",
  onChange,
  onBlur,
  onValidChange,
  disabled = false,
  readOnly = false,
  required = true,
  mobileOnly = true,
  showError = true,
  className = "",
  placeholder = "7123 456789",
}) {
  const [touched, setTouched] = useState(false);
  const e164 = useMemo(() => toE164(value) || (String(value || "").startsWith("+") ? String(value) : ""), [value]);
  const result = useMemo(
    () => validatePhone(e164 || value, { mobileOnly, required }),
    [e164, value, mobileOnly, required]
  );

  useEffect(() => {
    onValidChange?.(result.ok);
    // Parent callbacks are often inline; only re-run when validity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.ok]);

  const errorText = phoneErrorMessage(mobileOnly);
  const showInlineError = showError && touched && !result.ok;
  const describedBy = showInlineError && id ? `${id}-error` : undefined;

  if (readOnly) {
    return (
      <div className={`phone-field ${className}`.trim()}>
        <div className="phone-field-readonly">{e164 || value || "—"}</div>
      </div>
    );
  }

  return (
    <div className={`phone-field ${className}`.trim()}>
      <PhoneInput
        id={id}
        name={name}
        international
        defaultCountry="GB"
        countryCallingCodeEditable={false}
        limitMaxLength
        value={e164 || undefined}
        onChange={(next) => onChange?.(next || "")}
        onBlur={(event) => {
          setTouched(true);
          onBlur?.(event);
        }}
        disabled={disabled}
        placeholder={placeholder}
        numberInputProps={{
          id,
          name,
          autoComplete: "tel",
          inputMode: "tel",
          "aria-invalid": showInlineError,
          "aria-describedby": describedBy,
        }}
        className={showInlineError ? "phone-field-input is-invalid" : "phone-field-input"}
      />
      {showInlineError ? (
        <p id={describedBy} className="mt-1 text-xs text-rose-600" role="alert">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}
